/**
 * Shared accessibility primitives (BL-110). ONE focus/ARIA pattern, reused
 * across the DOM controls so we never scatter ad-hoc `tabindex`/keydown:
 *   - {@link makeKeyboardActivatable} turns a non-`<button>` element (e.g. the
 *     draft `<div>` card) into a button-role control operable by mouse AND
 *     keyboard (Enter/Space), mirroring a click;
 *   - {@link ensureA11yStyles} injects ONE global `:focus-visible` ring — the
 *     dark theme needs a deliberate, high-contrast focus indicator (WCAG 2.4.7),
 *     and the menus/overlays/cards all share it without per-module CSS;
 *   - {@link announce} mirrors transient canvas outcomes into a polite live
 *     region (see `GameBanner`) so a screen reader hears wave/breach/etc.
 *
 * Render-only and DOM-guarded (no-ops without a document). Colour comes from the
 * central palette (`COLORS`) — no inline gameplay hex.
 */
import { COLORS } from "../core/constants.ts";

const FOCUS_STYLE_ID = "a11y-focus-styles";
const LIVE_REGION_ID = "a11y-live-region";
const ALERT_REGION_ID = "a11y-alert-region";

/**
 * The shared focus ring. ICE_CYAN reads as a distinct "focus" affordance against
 * the green/red/amber content on the dark theme (≥3:1 vs adjacent — WCAG 1.4.11),
 * and an `outline` (not `box-shadow`) is NOT clipped by `overflow:hidden`
 * ancestors (the side rails scroll).
 */
export const A11Y_FOCUS_RING = `2px solid ${COLORS.ICE_CYAN}`;

function resolveDoc(doc?: Document): Document | undefined {
  return doc ?? (typeof document !== "undefined" ? document : undefined);
}

/**
 * Inject the global `:focus-visible` ring once (idempotent). Scoped to genuinely
 * interactive elements (native controls + anything given a button role / tabindex
 * by {@link makeKeyboardActivatable}), so the draft cards, end-screen buttons and
 * menus all share one ring. Safe to call repeatedly and without a DOM.
 */
export function ensureA11yStyles(doc?: Document): void {
  const d = resolveDoc(doc);
  if (!d || d.getElementById(FOCUS_STYLE_ID)) return;
  const style = d.createElement("style");
  style.id = FOCUS_STYLE_ID;
  style.textContent = `
    a[href]:focus-visible,
    button:focus-visible,
    input:focus-visible,
    select:focus-visible,
    textarea:focus-visible,
    [role="button"]:focus-visible,
    [tabindex]:not([tabindex="-1"]):focus-visible {
      outline: ${A11Y_FOCUS_RING};
      outline-offset: 2px;
      border-radius: 3px;
    }
  `;
  (d.head ?? d.documentElement).appendChild(style);
}

/**
 * Make a non-`<button>` element keyboard-operable AS a button: `role="button"` +
 * `tabIndex=0` + Enter/Space activation mirroring a click. Use ONLY on non-button
 * elements — a real `<button>`/`<a href>` already activates on Enter/Space, so
 * double-wiring it would fire twice. Idempotently ensures the shared focus ring.
 */
export function makeKeyboardActivatable(
  el: HTMLElement,
  onActivate: () => void,
): void {
  el.setAttribute("role", "button");
  if (!el.hasAttribute("tabindex")) el.tabIndex = 0;
  ensureA11yStyles(el.ownerDocument);
  el.addEventListener("click", onActivate);
  el.addEventListener("keydown", (e: KeyboardEvent) => {
    // Enter OR Space activates (the native <button> contract). Space is
    // preventDefault'd so the page doesn't also scroll.
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onActivate();
    }
  });
}

/**
 * Announce text to the single shared polite live region (BL-110), creating it on
 * first use. The screen-reader mirror of the canvas outcome banners — `GameBanner`
 * calls this so wave/breach/boss/clear telegraphs (canvas-only, invisible to AT)
 * are heard. Visually hidden; no-op without a DOM. Re-setting identical text is a
 * no-op for the reader (polite regions only announce on change) — acceptable.
 */
export function announce(text: string, doc?: Document): void {
  const d = resolveDoc(doc);
  if (!d || !text) return;
  let region = d.getElementById(LIVE_REGION_ID);
  if (!region) {
    region = d.createElement("div");
    region.id = LIVE_REGION_ID;
    region.setAttribute("role", "status");
    region.setAttribute("aria-live", "polite");
    region.setAttribute("aria-atomic", "true");
    // Visually hidden, still read by AT (the standard sr-only recipe).
    region.style.cssText =
      "position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;" +
      "overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;";
    (d.body ?? d.documentElement).appendChild(region);
  }
  region.textContent = text;
}

/**
 * Announce a CRITICAL, time-sensitive event to a separate ASSERTIVE live region
 * (BL-256). Unlike {@link announce} (polite — waits for the reader to finish),
 * an assertive region interrupts so the player learns immediately that the
 * mainframe took damage / a life was lost. Kept as its own region so a burst of
 * polite wave telegraphs can't bury the alert. Visually hidden; no-op without a
 * DOM. A trailing marker (via toggling) is unnecessary because callers pass a
 * fresh, changing message (e.g. the remaining integrity count).
 */
export function announceAssertive(text: string, doc?: Document): void {
  const d = resolveDoc(doc);
  if (!d || !text) return;
  let region = d.getElementById(ALERT_REGION_ID);
  if (!region) {
    region = d.createElement("div");
    region.id = ALERT_REGION_ID;
    region.setAttribute("role", "alert");
    region.setAttribute("aria-live", "assertive");
    region.setAttribute("aria-atomic", "true");
    region.style.cssText =
      "position:absolute;width:1px;height:1px;margin:-1px;padding:0;border:0;" +
      "overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;";
    (d.body ?? d.documentElement).appendChild(region);
  }
  region.textContent = text;
}
